import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ArrowUpRight, ChevronDown, ChevronRight, FileText, Package, FlaskConical, Zap } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { Client } from '../../types';
import { useAppStore } from '../../store';
import { clientAccountsReceivableARS } from '../../utils/accountsReceivable';

interface ReceivablesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  periodInvoices: any[];
  periodProductions: any[];
  periodSamples: any[];
  periodExpress: any[];
  periodExpressSamples: any[];
}

const ReceivablesDetailModal: React.FC<ReceivablesDetailModalProps> = ({
  isOpen,
  onClose,
  clients,
  periodInvoices,
  periodProductions,
  periodSamples,
  periodExpress,
  periodExpressSamples
}) => {
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});
  const { invoices, payments } = useAppStore();

  // Igual que la ficha del cliente: equivalente ARS del saldo actual
  const clientsWithCalculatedBalance = (clients || []).map((client) => ({
    ...client,
    calculatedBalance: clientAccountsReceivableARS(client.id, invoices, payments),
  }));

  // Filter clients with positive balance (using calculated balance)
  const clientsWithBalance = clientsWithCalculatedBalance.filter(client => client.calculatedBalance > 0);
  
  // Sort by calculated balance (highest first)
  const sortedClients = [...clientsWithBalance].sort((a, b) => b.calculatedBalance - a.calculatedBalance);
  
  // Calculate total receivables (using calculated balance)
  const totalReceivables = clientsWithBalance.reduce((sum, client) => sum + client.calculatedBalance, 0);
  
  // Calculate percentages
  const getPercentage = (value: number) => {
    if (totalReceivables === 0) return 0;
    return Math.round((value / totalReceivables) * 100);
  };

  // Toggle client expansion
  const toggleClient = (clientId: string) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  // Get client's invoices
  const getClientInvoices = (clientId: string) => {
    return (periodInvoices || []).filter(invoice => invoice.entity_id === clientId);
  };

  // Get client's productions
  const getClientProductions = (clientId: string) => {
    return (periodProductions || []).filter(production => production.client_id === clientId);
  };

  // Get client's samples
  const getClientSamples = (clientId: string) => {
    return (periodSamples || []).filter(sample => sample.client_id === clientId);
  };

  // Get client's express orders
  const getClientExpress = (clientId: string) => {
    return (periodExpress || []).filter(order => order.client_id === clientId);
  };

  // Get client's express samples
  const getClientExpressSamples = (clientId: string) => {
    return (periodExpressSamples || []).filter(sample => sample.client_id === clientId);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[100] max-h-[90vh] w-[95vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <ArrowUpRight className="mr-2 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            Detalle de Cuentas por Cobrar
          </Dialog.Title>
          
          <div className="space-y-6">
            {/* Total Receivables */}
            <div className="bg-emerald-50 dark:bg-emerald-900/30 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Total por Cobrar</p>
                  <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">{formatCurrency(totalReceivables)}</p>
                </div>
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800 rounded-full">
                  <ArrowUpRight className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </div>
            
            {/* Client List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Clientes con Saldo Pendiente</h3>
              
              {sortedClients.length > 0 ? (
                <div className="space-y-3">
                  {sortedClients.map((client) => {
                    const isExpanded = expandedClients[client.id];
                    const clientInvoices = getClientInvoices(client.id);
                    const clientProductions = getClientProductions(client.id);
                    const clientSamples = getClientSamples(client.id);
                    const clientExpress = getClientExpress(client.id);
                    const clientExpressSamples = getClientExpressSamples(client.id);
                    
                    return (
                      <div key={client.id} className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <button
                          onClick={() => toggleClient(client.id)}
                          className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          <div className="flex-1 text-left">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{client.name}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(client.calculatedBalance)}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{getPercentage(client.calculatedBalance)}% del total</p>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                          </div>
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600">
                            <div className="space-y-4">
                              {/* Facturas */}
                              {clientInvoices.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                    <FileText className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    Facturas ({clientInvoices.length})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {clientInvoices.map((invoice) => {
                                      const isUSD = invoice.currency === 'USD';
                                      const originalAmount = invoice.subtotal || invoice.total || 0;
                                      const convertedAmount = isUSD && invoice.exchange_rate ? originalAmount * invoice.exchange_rate : originalAmount;
                                      
                                      return (
                                        <div key={invoice.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                          <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Factura {invoice.number}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatArgentinaDate(invoice.date)}</p>
                                            {isUSD && (
                                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                💵 USD • TC: {invoice.exchange_rate} ARS/USD
                                              </p>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(convertedAmount)}</p>
                                            {isUSD && (
                                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Original: USD {originalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </p>
                                            )}
                                            {invoice.salesperson && (
                                              <p className="text-xs text-gray-500 dark:text-gray-400">Vendedor: {invoice.salesperson}</p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Producciones */}
                              {clientProductions.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                    <Package className="mr-2 h-4 w-4 text-purple-600 dark:text-purple-400" />
                                    Producciones ({clientProductions.length})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {clientProductions.map((production) => (
                                      <div key={production.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-white">Producción #{production.id}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatArgentinaDate(production.created_at)}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(production.production_value || 0)}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Muestras */}
                              {clientSamples.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                    <FlaskConical className="mr-2 h-4 w-4 text-pink-600 dark:text-pink-400" />
                                    Muestras ({clientSamples.length})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {clientSamples.map((sample) => (
                                      <div key={sample.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-white">Muestra #{sample.id}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatArgentinaDate(sample.created_at)}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(sample.sample_value || 0)}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
              )}

                              {/* Express */}
                              {clientExpress.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                    <Zap className="mr-2 h-4 w-4 text-orange-600 dark:text-orange-400" />
                                    Express ({clientExpress.length})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {clientExpress.map((order) => (
                                      <div key={order.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-white">Express #{order.id}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatArgentinaDate(order.date || order.created_at)}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(order.express_value || order.total || 0)}</p>
                                          {order.salesperson && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Vendedor: {order.salesperson}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Express Samples */}
                              {clientExpressSamples.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                    <FlaskConical className="mr-2 h-4 w-4 text-pink-600 dark:text-pink-400" />
                                    Muestras Express ({clientExpressSamples.length})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {clientExpressSamples.map((sample) => (
                                      <div key={sample.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-white">Muestra Express #{sample.id}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatArgentinaDate(sample.date || sample.created_at)}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(sample.sample_value || sample.total || 0)}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {clientInvoices.length === 0 && clientProductions.length === 0 && clientSamples.length === 0 && clientExpress.length === 0 && clientExpressSamples.length === 0 && (
                                <p className="text-center text-gray-500 dark:text-gray-400 py-2">No hay transacciones en este período</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="px-4 pb-4">
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div 
                              className="bg-emerald-600 dark:bg-emerald-500 h-2 rounded-full" 
                              style={{ width: `${getPercentage(client.calculatedBalance)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No hay cuentas por cobrar
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => onClose()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Cerrar
            </button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReceivablesDetailModal;